// The tale cards on Home — LIVE miniatures of the books, so the child sees them
// moving before anyone taps: the painted room drifts in depth, the hero breathes,
// the lantern light flickers, a few stars twinkle. Pure CSS on 3 images; no
// dependency on the (lazy) tale player or the games art.
import { useEffect, useState } from "react";
import { BookOpen, Lock, Play } from "lucide-react";
import { api } from "../api.js";
import { toFa } from "../format.js";
import { haptic } from "../host.js";
import { useLang } from "../../i18n.jsx";
import { BLACK, C, FONT, TALE, WHITE, alpha } from "../../design-system.js";
import { TALES } from "./catalog.js";

const CARD_CSS = `
.tc-card{position:relative;flex:0 0 auto;width:100%;aspect-ratio:16/11;border-radius:22px;overflow:hidden;cursor:pointer;border:none;padding:0;
  background:${TALE.night};box-shadow:0 14px 34px ${alpha(BLACK, .45)},inset 0 0 0 1px ${alpha(TALE.label, .18)};perspective:600px;font-family:inherit;text-align:start;
  -webkit-tap-highlight-color:transparent;transition:transform .2s}
.tc-card:active{transform:scale(.97)}
.tc-world{position:absolute;inset:0;transform-style:preserve-3d;animation:tcDrift 9s ease-in-out infinite}
@keyframes tcDrift{0%,100%{transform:rotateY(-3deg) rotateX(1deg)}50%{transform:rotateY(3deg) rotateX(-1deg)}}
.tc-bg{position:absolute;inset:-10%;background-size:cover;transform:translateZ(-60px) scale(1.12);filter:brightness(.62) saturate(1.1)}
.tc-hero{position:absolute;bottom:-4%;height:84%;transform:translateZ(30px);transform-origin:50% 100%;animation:tcBreathe 3.4s ease-in-out infinite}
.tc-side{position:absolute;bottom:2%;height:34%;transform:translateZ(45px);animation:tcBob 2.8s ease-in-out infinite}
@keyframes tcBreathe{0%,100%{scale:1 1}50%{scale:1.015 1.03}}
@keyframes tcBob{0%,100%{translate:0 0}50%{translate:0 -7%}}
.tc-glow{position:absolute;width:55%;aspect-ratio:1;border-radius:50%;pointer-events:none;
  background:radial-gradient(closest-side,${alpha(TALE.ember, .55)},${alpha(TALE.lanternDeep, .15)} 50%,transparent);animation:tcFlick 2.6s ease-in-out infinite}
@keyframes tcFlick{0%,100%{opacity:.75}45%{opacity:1}70%{opacity:.85}}
.tc-tw{position:absolute;width:5px;height:5px;border-radius:50%;background:${TALE.spark};box-shadow:0 0 8px ${TALE.lantern};animation:tcTw 2.4s ease-in-out infinite}
@keyframes tcTw{0%,100%{opacity:.2;transform:scale(.6)}50%{opacity:1;transform:scale(1.2)}}
.tc-shade{position:absolute;inset:0;background:linear-gradient(180deg,${alpha(TALE.nightInk, 0)} 35%,${alpha(TALE.nightInk, .88)});pointer-events:none}
.tc-play{display:inline-flex;align-items:center;gap:6px;background:linear-gradient(135deg,${TALE.lantern},${TALE.lanternDeep});color:${TALE.ink};border-radius:999px;
  padding:8px 16px;font-weight:800;font-size:14px;box-shadow:0 4px 18px ${alpha(TALE.lanternDeep, .45)};animation:tcPulse 2s ease-in-out infinite}
@keyframes tcPulse{0%,100%{transform:scale(1)}50%{transform:scale(1.06)}}
.tc-strip{display:flex;gap:12px;overflow-x:auto;scroll-snap-type:x mandatory;padding:4px 2px 14px;margin:0 -2px;scrollbar-width:none;-webkit-overflow-scrolling:touch}
.tc-strip::-webkit-scrollbar{display:none}
.tc-strip > div{flex:0 0 80%;scroll-snap-align:start}
@media (prefers-reduced-motion: reduce){.tc-world,.tc-hero,.tc-side,.tc-play{animation:none}}
`;

function useCardCss() {
  useEffect(() => {
    if (document.getElementById("zg-tale-card-css")) return;
    const el = document.createElement("style");
    el.id = "zg-tale-card-css";
    el.textContent = CARD_CSS;
    document.head.appendChild(el);
  }, []);
}

export function TaleCard({ tale, onOpen, lang }) {
  useCardCss();
  const meta = TALES[tale.key];
  if (!meta) return null;
  const { cover } = meta;
  const base = (f) => `${meta.base}${f}?v=${meta.v || 1}`;
  const title = lang === "en" ? tale.title_en : tale.title_fa;
  return (
    <button className="tc-card" onClick={() => { haptic("light"); onOpen(tale); }}>
      <div className="tc-world">
        <div className="tc-bg" style={{ backgroundImage: `url(${base(cover.bg)})`, backgroundPosition: cover.bgPos }} />
        {[[12, 18], [22, 30], [8, 40], [30, 12]].map(([x, y], i) => <span key={i} className="tc-tw" style={{ left: `${x}%`, top: `${y}%`, animationDelay: `${i * 0.6}s` }} />)}
        {cover.glow && <div className="tc-glow" style={{ left: "30%", top: "8%" }} />}
        <img className="tc-hero" src={base(cover.hero)} alt="" loading="lazy" decoding="async" style={{ left: "52%" }} />
        {cover.side && <img className="tc-side" src={base(cover.side)} alt="" loading="lazy" decoding="async" style={{ left: "5%", bottom: "30%", height: "30%", animationDelay: ".5s" }} />}
      </div>
      <div className="tc-shade" />
      <div style={{ position: "absolute", top: 12, insetInlineStart: 12, display: "flex", gap: 6 }}>
        <span style={{ background: alpha(TALE.night, .6), color: TALE.label, borderRadius: 999, padding: "4px 11px", fontSize: 12, fontWeight: 700 }}>
          {lang === "en" ? "Interactive tale" : "قصهٔ تعاملی"}
        </span>
        {tale.free && !tale.locked && (
          <span style={{ background: C.teal, color: TALE.tealInk, borderRadius: 999, padding: "4px 11px", fontSize: 12, fontWeight: 800 }}>
            {lang === "en" ? "Free" : "رایگان"}
          </span>
        )}
      </div>
      <div style={{ position: "absolute", insetInline: 14, bottom: 12, display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: TALE.title, fontSize: 17, fontWeight: 800, textShadow: `0 2px 10px ${alpha(BLACK, .7)}` }}>{title}</div>
          <div style={{ color: TALE.sub, fontSize: 12.5, marginTop: 2 }}>{lang === "en" ? tale.skill_en : tale.skill_fa}</div>
        </div>
        {tale.locked
          ? <span className="tc-play" style={{ animation: "none", background: alpha(WHITE, .18), color: WHITE }}><Lock size={14} /> {lang === "en" ? "Members" : "با اشتراک"}</span>
          : <span className="tc-play"><Play size={14} fill={TALE.ink} /> {lang === "en" ? "Play" : "بخوان"}</span>}
      </div>
    </button>
  );
}

// Warm the 3D library while the parent is still on Home: the WebGL chunk (three.js
// + the room, all code — no room images) and the shelf's covers. Idle time only, so
// Home's own first paint never waits for it. Opening a BOOK still loads only that
// book's pictures and voices (tales/3d/assets.js).
let warmed = false;
function warmLibrary(tales) {
  if (warmed || typeof window === "undefined") return;
  warmed = true;
  const go = () => {
    import("./Library.jsx").catch(() => {});
    tales.forEach(t => { const m = TALES[t.key]; if (!m) return; [m.coverFile, m.heroFile].filter(Boolean).forEach(f => { const i = new Image(); i.src = `${m.base}${f}?v=${m.v || 1}`; }); });
  };
  (window.requestIdleCallback || ((fn) => setTimeout(fn, 1200)))(go);
}

// How many tales Home previews (the shelf in the library holds them all).
const PREVIEW = 5;

// «قصه‌های تعاملی» on Home: a swipeable row of SEVERAL tales (it used to be one
// card previewing only the first — Arman — product owner, 2026-10-27). Each card
// opens the 3D library on ITS book; «کتابخانه» opens it on the first. Ranked per
// child by the server; with no child yet, the catalog's own order
// (GET /children/tales). The first ones are free for everyone.
export function TaleShelf({ child, onOpen }) {
  const { t, lang } = useLang();
  const [tales, setTales] = useState(null);
  useEffect(() => {
    let alive = true;
    api(child?.id ? `/children/${child.id}/tales` : "/children/tales")
      .then(d => alive && setTales((d.tales || []).filter(x => TALES[x.key])))
      .catch(() => alive && setTales([]));
    return () => { alive = false; };
  }, [child?.id, child?.subscribed]);
  useEffect(() => { if (tales?.length) warmLibrary(tales); }, [tales]);
  if (!tales?.length) return null;
  return (
    <div style={{ marginBottom: 6, fontFamily: FONT }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, paddingInline: 4 }}>
        <span style={{ color: C.cream, fontSize: 15.5, fontWeight: 800 }}>
          {t("قصه‌های تعاملی")} <span style={{ color: C.textMid, fontSize: 13, fontWeight: 600 }}>· {t("{n} کتاب", { n: toFa(tales.length) })}</span>
        </span>
        <button className="press" onClick={() => { haptic("light"); onOpen(tales[0], child); }}
          style={{ background: "none", border: "none", color: C.gold, fontFamily: FONT, fontSize: 13.5, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 }}>
          <BookOpen size={15} /> {t("کتابخانه")}
        </button>
      </div>
      <div className="tc-strip">
        {tales.slice(0, PREVIEW).map(tale => (
          <div key={tale.key}><TaleCard tale={tale} lang={lang} onOpen={() => onOpen(tale, child)} /></div>
        ))}
      </div>
    </div>
  );
}

